import { Partner, PartnerFamilienstand, PartnerGeschlecht } from '@mng/domain';
import { BooleanField, DateField, ModelGroup, NumberField, StringField, ValueOptions } from '@mng/model';
import { GvStammdaten } from '../gvangebot.model.types';
import { UvStammdaten } from '../uvangebot.model.types';
import { SvStammdaten } from '../svangebot.model.types';

export type StammdatenCommon = GvStammdaten | UvStammdaten | SvStammdaten;

export function mapPartnerStammdatenToPersonStammdaten(
  partner: Partner,
  withDesc = false,
  withUvDetails = false
): StammdatenCommon {
  const stammdaten: StammdatenCommon = {};
  stammdaten.partnerNr = partner.partnerNr;
  if (withUvDetails) {
    (<UvStammdaten>stammdaten).juristischePerson = partner.juristischePerson;
  }
  stammdaten.vorname = partner.vorname;
  stammdaten.name = partner.name;
  stammdaten.gebdatum = partner.gebdatum;
  stammdaten.geschlecht = partner.geschlecht;
  stammdaten.titel = partner.titel;
  stammdaten.titelNachgestellt = partner.postTitel;
  stammdaten.berufstitel = partner.berufstitel;
  stammdaten.timeStamp = partner.timeStamp;
  stammdaten.geburtsland = partner.geburtsland;
  stammdaten.familienstand = partner.familienstand;
  stammdaten.nationalitaet = partner.nationalitaet;
  stammdaten.juristischePerson = partner.juristischePerson;
  stammdaten.versicherungsnummer = partner.sozversnr ? partner.sozversnr.versicherungsnummer : null;
  stammdaten.sozversanstalt = partner.sozversanstalt ? partner.sozversanstalt.hostKey : null;
  stammdaten.merkurEmployee = partner.merkurEmployee;
  if (withDesc) {
    (<GvStammdaten>stammdaten).sozversanstaltName = partner.sozversanstalt ? partner.sozversanstalt.name : null;
  }
  if (partner.beruf) {
    stammdaten.berufsbezeichnung = partner.beruf.beruf ? partner.beruf.beruf.hostKey : null;
    stammdaten.beschaeftigungsart = partner.beruf.beschaeftigungsart ? partner.beruf.beschaeftigungsart.hostKey : null;
    stammdaten.beschaeftigtBei = partner.beruf.beschaeftigtBei;
  }
  if (partner.adresse) {
    stammdaten.adresse = {};
    stammdaten.adresse.hausnr = partner.adresse.hausnr;
    stammdaten.adresse.strasse = partner.adresse.strasse;
    stammdaten.adresse.ikz = partner.adresse.ikz;
    stammdaten.adresse.ort = partner.adresse.ort;
    stammdaten.adresse.plz = partner.adresse.plz;
  }
  return stammdaten;
}

export const familienStandValueOptions: ValueOptions = new ValueOptions([
  { name: 'ledig', value: PartnerFamilienstand.LEDIG },
  {
    name: 'Lebensgemeinschaft',
    value: PartnerFamilienstand.LEBENSGEMEINSCHAFT
  },
  { name: 'verheiratet', value: PartnerFamilienstand.VERHEIRATET },
  { name: 'verwitwet', value: PartnerFamilienstand.VERWITWET },
  { name: 'geschieden', value: PartnerFamilienstand.GESCHIEDEN },
  { name: 'unbekannt', value: PartnerFamilienstand.UNBEKANNT }
]);

export const geschlechtValueOptions: ValueOptions = new ValueOptions([
  { name: 'weiblich', value: PartnerGeschlecht.W },
  { name: 'männlich', value: PartnerGeschlecht.M }
]);

export class StammdatenAdresseBase {
  hausnr: StringField;
  ikz: StringField;
  ort: StringField;
  plz: StringField;
  strasse: StringField;

  adresse: ModelGroup;

  constructor(readOnly: boolean, id = 'adresse', interfaceName = 'AdressModelData') {
    this.hausnr = new StringField('hausnr', 'Haus Nr.').withReadOnly(readOnly);
    this.ikz = new StringField('ikz', 'IKZ').withReadOnly(readOnly);
    this.ort = new StringField('ort', 'Ort').withReadOnly(readOnly);
    this.plz = new StringField('plz', 'PLZ').withReadOnly(readOnly);
    this.strasse = new StringField('strasse', 'Straße').withReadOnly(readOnly);
    this.adresse = new ModelGroup(id, 'Adresse')
      .withInterfaceName(interfaceName)
      .addItems(this.strasse, this.hausnr, this.ikz, this.ort, this.plz);
  }
}

export class StammdatenBase {
  partnerNr: NumberField;
  vorname: StringField;
  titel: StringField;
  titelNachgestellt: StringField;
  berufstitel: StringField;
  timeStamp: StringField;
  name: StringField;
  gebdatum: DateField;
  familienstand: StringField;
  geschlecht: StringField;
  geburtsland: StringField;
  svAnstalt: NumberField;
  svAnstaltName: StringField;
  svNummer: NumberField;
  berufsbezeichnung: NumberField;
  beschaeftigungsart: NumberField;
  beschaeftigtBei: StringField;
  nationalitaet: StringField;
  juristischePerson: BooleanField;
  merkurEmployee: BooleanField;

  stammDatenAdresse: StammdatenAdresseBase;

  init(readOnly = true, withDesc = false) {
    this.partnerNr = new NumberField('partnerNr', 'Partnernummer').withReadOnly();
    this.juristischePerson = new BooleanField('juristischePerson', 'Juristische Person').withReadOnly();
    this.titel = new StringField('titel', 'Titel').withReadOnly(readOnly);
    this.titelNachgestellt = new StringField('titelNachgestellt', 'Nachgestellter Titel').withReadOnly(readOnly);
    this.berufstitel = new StringField('berufstitel', 'Berufstitel').withReadOnly(readOnly);
    this.timeStamp = new StringField('timeStamp', 'Letzte Bearbeitung relevanter Daten').withReadOnly();
    this.vorname = new StringField('vorname', 'Vorname').withReadOnly(readOnly);
    this.name = new StringField('name', 'Familienname/Firma').withReadOnly(readOnly);
    this.gebdatum = new DateField('gebdatum', 'Geb.Datum').withReadOnly(readOnly);
    this.familienstand = new StringField('familienstand', 'Familienstand').withReadOnly(readOnly);
    this.geschlecht = new StringField('geschlecht', 'Geschlecht').withReadOnly(readOnly);
    this.geburtsland = new StringField('geburtsland', 'Geburtsland').withReadOnly(readOnly);
    this.svAnstalt = new NumberField('sozversanstalt', 'Sozialversicherung wählen').withReadOnly(readOnly);
    if (withDesc) {
      this.svAnstaltName = new StringField('sozversanstaltName', 'Krankenkasse').withReadOnly(readOnly);
    }
    this.svNummer = new NumberField('versicherungsnummer', 'Versicherungsnummer').withReadOnly(readOnly);

    this.berufsbezeichnung = new NumberField('berufsbezeichnung', 'Berufsbezeichnung').withReadOnly(readOnly);
    this.beschaeftigungsart = new NumberField('beschaeftigungsart', 'Art der Beschäftigung').withReadOnly(readOnly);
    this.beschaeftigtBei = new StringField('beschaeftigtBei', 'Arbeitgeber').withReadOnly(readOnly);
    this.nationalitaet = new StringField('nationalitaet', 'Nationalität').withReadOnly(readOnly);
    this.juristischePerson = new BooleanField('juristischePerson', 'Juristisch Person').withReadOnly(readOnly);

    this.stammDatenAdresse = new StammdatenAdresseBase(readOnly);
    this.merkurEmployee = new BooleanField('merkurEmployee', 'Merkur Mitarbeiter').withReadOnly(readOnly);
  }

  get adresse(): ModelGroup {
    return this.stammDatenAdresse?.adresse;
  }

  get hausnr(): StringField {
    return this.stammDatenAdresse?.hausnr;
  }

  get ikz(): StringField {
    return this.stammDatenAdresse?.ikz;
  }

  get ort(): StringField {
    return this.stammDatenAdresse?.ort;
  }

  get plz(): StringField {
    return this.stammDatenAdresse.plz;
  }

  get strasse(): StringField {
    return this.stammDatenAdresse?.strasse;
  }

  createStammdatenModelGroup(): ModelGroup {
    const stammdatenModelGroup: ModelGroup = new ModelGroup('stammdaten', 'Stammdaten');
    stammdatenModelGroup.addItems(
      this.partnerNr,
      this.geschlecht,
      this.vorname,
      this.titel,
      this.titelNachgestellt,
      this.berufstitel,
      this.timeStamp,
      this.name,
      this.gebdatum,
      this.familienstand,
      this.berufsbezeichnung,
      this.beschaeftigungsart,
      this.beschaeftigtBei,
      this.geburtsland,
      this.nationalitaet,
      this.juristischePerson,
      this.svAnstalt,
      this.merkurEmployee,
      this.svNummer,
      this.adresse
    );
    if (this.svAnstaltName) {
      stammdatenModelGroup.addItems(this.svAnstaltName);
    }
    return stammdatenModelGroup;
  }
}
